import React from 'react';

import { NotificationsPanel } from '@decisiv/ui-components';
import { StackingContextProvider } from '@decisiv/ui-components/lib/utils/useStackingContext';

/**
 * The app's toast layer. Mount this instead of NotificationsPanel directly.
 *
 * Above the wizards. The bulk wizard pauses the event poll while it is open, but
 * the single-user one does not, so a toast can still arrive over a modal and must
 * not be painted behind it.
 */
export const TOAST_Z_INDEX = 1000;

/**
 * The StackingContextProvider is what fixes PD-4722, where the hint on a toast's
 * close ("X") button rendered behind the toast instead of over it.
 *
 * KeyDS gives a Popover `currentZIndex + zIndex`, where currentZIndex comes from
 * the nearest StackingContextProvider. NotificationsPanel does not supply one, and
 * Notification hardcodes its close hint's Popover to zIndex 100, so the hint
 * computes 0 + 100 and loses to the panel's own 1000. Providing the context here
 * makes it 1000 + 100 instead, clearing the panel. Modal fixes its own close hint
 * the same way, by wrapping its subtree at its own zIndex.
 *
 * Deliberately NOT done with a global CSS rule raising the portaled
 * `#key-popovers > div > div[class*="PopoverContent"]`. That selector outranks
 * styled-components' single class for *every* portaled overlay app-wide, including
 * the wizard dropdowns that CreateUserWizard/layers.js pins at 101 (PD-4064) to
 * keep them under this layer, and it is untestable in our jest setup because
 * createGlobalStyle never injects under jsdom. It would also break if KeyDS ever
 * renamed that styled component, silently restoring the bug with green tests.
 *
 * useStackingContext is not re-exported from the package root, hence the deep
 * import (as with lib/utils/rgbaToHex elsewhere). It renders no DOM of its own.
 */
const Toasts = () => (
  <StackingContextProvider zIndex={TOAST_Z_INDEX}>
    <NotificationsPanel marginTop={4} zIndex={TOAST_Z_INDEX} />
  </StackingContextProvider>
);

export default Toasts;
